import { compact, orderBy } from 'lodash-es';
import { extractPureTextFromRichText } from 'seagull/components/RichTextEditor/RichTextEditor';
import { getTemplateType } from "../../../components/AgreementStudioPage/AgreementStudioModal/IntroMessage/IntroMessage.utils";
import { approvalMethodNoticePeriodModel, introVideoModel } from "../../../models";
import styles from "./EditDraftsTableRow.module.scss";
import { EditDraftsTableColumns, ColumnSortId } from "./types";
export const editDraftsTableHeaderColumnDefs = visibleColumns => compact([{
  value: EditDraftsTableColumns.PrimaryContact,
  sortId: ColumnSortId.PrimaryContact,
  width: 240,
  sortable: true,
  sticky: true
}, visibleColumns[EditDraftsTableColumns.AdditionalRecipients] && {
  value: EditDraftsTableColumns.AdditionalRecipients,
  sortId: ColumnSortId.AdditionalRecipients,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.AgreementName] && {
  value: EditDraftsTableColumns.AgreementName,
  sortId: ColumnSortId.AgreementName,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.EffectiveDate] && {
  value: EditDraftsTableColumns.EffectiveDate,
  sortId: ColumnSortId.EffectiveDate,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.ModifiedBy] && {
  value: EditDraftsTableColumns.ModifiedBy,
  sortId: ColumnSortId.ModifiedBy,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.LastModified] && {
  value: EditDraftsTableColumns.LastModified,
  sortId: ColumnSortId.LastModified,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.ExpirationDate] && {
  value: EditDraftsTableColumns.ExpirationDate,
  sortId: ColumnSortId.ExpirationDate,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.IntroductoryMessage] && {
  value: EditDraftsTableColumns.IntroductoryMessage,
  sortId: ColumnSortId.IntroductoryMessage,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.IntroductoryVideo] && {
  value: EditDraftsTableColumns.IntroductoryVideo,
  sortId: ColumnSortId.IntroductoryVideo,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.TermsOfService] && {
  value: EditDraftsTableColumns.TermsOfService,
  sortId: ColumnSortId.TermsOfService,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.AgreementNote] && {
  value: EditDraftsTableColumns.AgreementNote,
  sortId: ColumnSortId.AgreementNote,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.AutoApproveAmendments] && {
  value: EditDraftsTableColumns.AutoApproveAmendments,
  sortId: ColumnSortId.AutoApproveAmendments,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.AutoApproveOutOfScopeCharges] && {
  value: EditDraftsTableColumns.AutoApproveOutOfScopeCharges,
  sortId: ColumnSortId.AutoApproveOutOfScopeCharges,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.NumberOfDaysUntilPaymentIsDue] && {
  value: EditDraftsTableColumns.NumberOfDaysUntilPaymentIsDue,
  sortId: ColumnSortId.NumberOfDaysUntilPaymentIsDue,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.PaymentMethodRequiredForSigning] && {
  value: EditDraftsTableColumns.PaymentMethodRequiredForSigning,
  sortId: ColumnSortId.PaymentMethodRequiredForSigning,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.CreditCardProcessingFees] && {
  value: EditDraftsTableColumns.CreditCardProcessingFees,
  sortId: ColumnSortId.CreditCardProcessingFees,
  width: 200,
  sortable: true
}, visibleColumns[EditDraftsTableColumns.ManualApprovalOfEachPayment] && {
  value: EditDraftsTableColumns.ManualApprovalOfEachPayment,
  sortId: ColumnSortId.ManualApprovalOfEachPayment,
  width: 200,
  sortable: true
}]);
export const getUICompanyName = (draft, contact) => draft.isSample() ? 'Anchor (Sample)' : contact ? contact.companyName() : '';
export const getAgreementName = draft => draft.title() || '(Unnamed Draft)';
export const getModifiedBy = draft => {
  const lastModifiedBy = draft.lastModifiedBy();
  return lastModifiedBy ? compact([lastModifiedBy.firstName, lastModifiedBy.lastName]).join(' ') : '';
};
export const getFirstLegalTermsDocument = draft => {
  const legalTermsDocuments = draft.legalTermsDocuments();
  return legalTermsDocuments.find(doc => doc !== null);
};
function textSearchDraft(search, draft, contact) {
  return "".concat(getUICompanyName(draft, contact), " ").concat(draft.title()).toLowerCase().includes(search);
}
export const getServicesTableDefs = () => [{
  width: 250,
  value: 'Service Name'
}, {
  width: 220,
  value: 'Billing'
}, {
  width: 120,
  value: 'Rate'
}, {
  width: 220,
  value: 'Description'
}, {
  width: 300,
  value: 'Advanced Settings'
}];
export const makeEditableTableItem = value => ({
  className: styles.cell,
  value
});
export function filterDrafts(drafts, getContact, search) {
  return drafts.filter(draft => {
    const normalizedSearch = search.trim().toLowerCase();
    const contact = getContact(draft);
    return textSearchDraft(normalizedSearch, draft, contact);
  });
}
const sortPropertiesBySortId = {
  [ColumnSortId.PrimaryContact]: {
    iteratees: [_ref => {
      let {
        lowercaseCompanyName
      } = _ref;
      return lowercaseCompanyName;
    }, _ref2 => {
      let {
        contactName
      } = _ref2;
      return contactName;
    }],
    reverseOrder: true
  },
  [ColumnSortId.AdditionalRecipients]: {
    iteratees: [_ref3 => {
      let {
        draft
      } = _ref3;
      return draft.reviewers().length;
    }]
  },
  [ColumnSortId.AgreementName]: {
    iteratees: [_ref4 => {
      let {
        draft
      } = _ref4;
      return getAgreementName(draft).toLowerCase();
    }],
    reverseOrder: true
  },
  [ColumnSortId.EffectiveDate]: {
    iteratees: [_ref5 => {
      var _draft$effectiveDateA;
      let {
        draft
      } = _ref5;
      return ((_draft$effectiveDateA = draft.effectiveDateAsDate()) === null || _draft$effectiveDateA === void 0 ? void 0 : _draft$effectiveDateA.getTime()) || 0;
    }],
    reverseOrder: true
  },
  [ColumnSortId.ModifiedBy]: {
    iteratees: [_ref6 => {
      let {
        draft
      } = _ref6;
      return getModifiedBy(draft).toLowerCase();
    }]
  },
  [ColumnSortId.LastModified]: {
    iteratees: [_ref7 => {
      let {
        draft
      } = _ref7;
      return draft.lastModifiedOn();
    }]
  },
  [ColumnSortId.ExpirationDate]: {
    iteratees: [_ref8 => {
      let {
        draft
      } = _ref8;
      return draft.expirationDaysAfterSending() || 0;
    }]
  },
  [ColumnSortId.IntroductoryMessage]: {
    iteratees: [_ref9 => {
      let {
        introductoryMessageType
      } = _ref9;
      return introductoryMessageType;
    }],
    reverseOrder: true
  },
  [ColumnSortId.IntroductoryVideo]: {
    iteratees: [_ref10 => {
      let {
        draft
      } = _ref10;
      const introVideo = draft.introVideo();
      return introVideo ? introVideoModel.videoUrl(introVideo) || '' : '';
    }]
  },
  [ColumnSortId.TermsOfService]: {
    iteratees: [_ref11 => {
      let {
        lowercaseLegalTermsName
      } = _ref11;
      return lowercaseLegalTermsName;
    }],
    reverseOrder: true
  },
  [ColumnSortId.AgreementNote]: {
    iteratees: [_ref12 => {
      let {
        extraClausePureText
      } = _ref12;
      return extraClausePureText;
    }],
    reverseOrder: true
  },
  [ColumnSortId.AutoApproveAmendments]: {
    iteratees: [_ref13 => {
      let {
        draft
      } = _ref13;
      const amendmentsApprovalMethod = draft.amendmentsApprovalMethod();
      return amendmentsApprovalMethod && approvalMethodNoticePeriodModel.noticePeriodDays(amendmentsApprovalMethod) || 0;
    }]
  },
  [ColumnSortId.AutoApproveOutOfScopeCharges]: {
    iteratees: [_ref14 => {
      let {
        draft
      } = _ref14;
      const invoicesApprovalMethod = draft.invoicesApprovalMethod();
      return invoicesApprovalMethod && approvalMethodNoticePeriodModel.noticePeriodDays(invoicesApprovalMethod) || 0;
    }]
  },
  [ColumnSortId.NumberOfDaysUntilPaymentIsDue]: {
    iteratees: [_ref15 => {
      let {
        draft
      } = _ref15;
      return draft.netTerms();
    }]
  },
  [ColumnSortId.PaymentMethodRequiredForSigning]: {
    iteratees: [_ref16 => {
      let {
        draft
      } = _ref16;
      return draft.requirePaymentMethod() ? 1 : 0;
    }]
  },
  [ColumnSortId.CreditCardProcessingFees]: {
    iteratees: [_ref17 => {
      let {
        draft
      } = _ref17;
      return !draft.coverCreditCardFees();
    }, _ref18 => {
      let {
        draft,
        lowercaseCompanyName
      } = _ref18;
      const isPaidByClient = !draft.coverCreditCardFees();
      return isPaidByClient ? lowercaseCompanyName : '';
    }],
    reverseOrder: true
  },
  [ColumnSortId.ManualApprovalOfEachPayment]: {
    iteratees: [_ref19 => {
      let {
        draft
      } = _ref19;
      return draft.allowManualPayments() ? 1 : 0;
    }]
  }
};
export function sortDrafts(_ref20) {
  let {
    drafts,
    sortId,
    sort,
    introductoryMessages,
    getContact
  } = _ref20;
  const enrichedDrafts = drafts.map(draft => {
    const contact = getContact(draft);
    return {
      draft,
      contact,
      lowercaseLegalTermsName: (() => {
        if (sortId === ColumnSortId.TermsOfService) {
          const legalTerms = getFirstLegalTermsDocument(draft);
          return (legalTerms ? legalTerms.displayName : '').toLowerCase();
        }
        return '';
      })(),
      extraClausePureText: sortId === ColumnSortId.AgreementNote ? extractPureTextFromRichText(draft.extraClause()).trim() : '',
      lowercaseCompanyName: [ColumnSortId.PrimaryContact, ColumnSortId.CreditCardProcessingFees].includes(sortId) ? getUICompanyName(draft, contact).toLowerCase() : '',
      contactName: sortId === ColumnSortId.PrimaryContact && contact ? contact.fullName().toLowerCase() : '',
      introductoryMessageType: sortId === ColumnSortId.IntroductoryMessage ? getTemplateType(draft.customNotificationText(), introductoryMessages) : ''
    };
  });
  const {
    iteratees,
    reverseOrder
  } = sortPropertiesBySortId[sortId];
  const sortingOrder = reverseOrder ? sort === 'asc' ? 'desc' : 'asc' : sort;
  const orders = new Array(iteratees.length).fill(sortingOrder);
  return orderBy(enrichedDrafts, iteratees, orders).map(d => d.draft);
}